import React from 'react';
import { Thermometer, Leaf, MapPin, TrendingUp, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const EnvironmentalMetrics = ({ metrics }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NO', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const avgTemp = metrics.length > 0
    ? (metrics.reduce((sum, m) => sum + m.temperature_celsius, 0) / metrics.length).toFixed(1)
    : 0;

  const totalCO2 = metrics.length > 0
    ? metrics[metrics.length - 1].co2_emissions_kg
    : 0;

  return (
    <div className="space-y-6" data-testid="environmental-metrics">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Leaf className="w-5 h-5 text-slate-700" />
            <span className="text-xs text-slate-700 uppercase tracking-wider font-medium">Total CO₂</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1 px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-xs cursor-help border border-slate-300">
                    Est
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Estimated (PoC)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <p className="text-2xl font-bold text-slate-800">{totalCO2} kg</p>
          <p className="text-xs text-slate-600 mt-1">Cumulative emissions</p>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Thermometer className="w-5 h-5 text-slate-700" />
            <span className="text-xs text-slate-700 uppercase tracking-wider font-medium">Avg Temp</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{avgTemp}°C</p>
          <p className="text-xs text-slate-600 mt-1">Cold chain maintained</p>
        </div>
      </div>

      {/* Metrics Timeline */}
      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3 uppercase tracking-wider">Metric Events</h3>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {metrics.map((metric, idx) => (
            <div
              key={metric.id}
              className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:border-slate-300 transition-colors"
              data-testid={`metric-${idx}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900 capitalize mb-1">
                    {metric.event_type.replace('_', ' ')}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <MapPin className="w-3 h-3" />
                    <span>{metric.location}</span>
                  </div>
                </div>
                <span className="text-xs text-slate-500">{formatDate(metric.timestamp)}</span>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-slate-100 rounded border border-slate-200">
                    <Thermometer className="w-4 h-4 text-slate-700" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Temperature</p>
                    <p className="text-sm font-semibold text-slate-800">{metric.temperature_celsius}°C</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="p-2 bg-slate-100 rounded border border-slate-200">
                    <Leaf className="w-4 h-4 text-slate-700" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-slate-500">CO₂ Emissions</p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs cursor-help border border-slate-300">
                              Est
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Estimated (PoC)</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className="text-sm font-semibold text-slate-800">{metric.co2_emissions_kg} kg</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* IoT Indicator */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-slate-100 rounded-lg border border-slate-200">
            <TrendingUp className="w-5 h-5 text-slate-700" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800 mb-1">IoT Sensor Data</p>
            <p className="text-xs text-slate-700 leading-relaxed">
              Environmental metrics collected via IoT sensors at each checkpoint.
              Real-time temperature and location monitoring ensures cold chain compliance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalMetrics;
